import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts';
import { adminService } from '../services/adminApi';
import { Calendar } from 'lucide-react';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-4 border border-blue-100 shadow-xl rounded-xl max-w-xs z-50">
        <p className="font-bold text-gray-900 mb-2 border-b border-gray-100 pb-1">
          {new Date(label).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
        </p>
        <p className="text-blue-600 font-bold mb-2 text-sm">{data.count} Hymns Sung</p>
        <div className="max-h-32 overflow-y-auto custom-scrollbar">
          <ul className="space-y-1">
            {data.titles.map((title, idx) => (
              <li key={idx} className="text-xs text-gray-600 truncate flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-300 mr-2 flex-shrink-0"></span>
                {title}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return null;
};

const RecentActivityChart = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await adminService.getRecentActivity();
        // Reverse to show oldest to newest left to right
        setData([...result].reverse());
      } catch (error) {
        console.error("Failed to fetch recent activity", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-2xl animate-pulse">
        <div className="text-gray-400 font-medium">Loading chart data...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="h-64 flex flex-col items-center justify-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
        <Calendar className="text-gray-300 mb-2" size={32} />
        <div className="text-gray-400 font-medium">No recent activity recorded</div>
      </div>
    );
  }

  return (
    <div className="w-full h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{
            top: 10,
            right: 10,
            left: -20,
            bottom: 0,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
          <XAxis 
            dataKey="date" 
            tickFormatter={(date) => new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            stroke="#9ca3af"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
            dy={10}
          />
          <YAxis 
            stroke="#9ca3af" 
            tick={{ fontSize: 11, fill: '#6b7280' }} 
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#eff6ff' }} />
          <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={50}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={index === data.length - 1 ? '#2563eb' : '#93c5fd'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RecentActivityChart;
